import { Button, Chip, Spinner } from "@heroui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardCheck, History, Lock, RotateCcw } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import AuthorizedButton from "@/components/common/AuthorizedButton";
import PageHeader from "@/components/common/PageHeader";
import { useResizableSplit } from "@/hooks/useResizableSplit";
import { useAuth } from "@/lib/auth-context";
import { canWrite } from "@/lib/authorization";
import { showError, showSuccess } from "@/lib/toast";
import { createQuotationItemDrawing } from "@/features/drawings/api";
import { getQuotation, listQuotationItems, updateQuotationItem } from "./api";
import ItemDrawingPanel from "./ItemDrawingPanel";
import ItemPastRecordsDialog from "./ItemPastRecordsDialog";
import ItemProcessPanel from "./ItemProcessPanel";

/**
 * 見積明細の見積画面。**左に図面、右に工程ごとの金額**を並べる。
 *
 * 図面の明細の単価は工程別の見積の合算で決まる（[[quotation_items]]）。金額は
 * 図面を見ないと決められないので、図面を出したまま金額を入れられる形にしてある。
 * 明細の一覧で行をクリックするとここに来る。
 *
 * 左右の比率は境目のドラッグで変えられ、次に開くときも保たれる
 * （[[useResizableSplit]]）。図面を大きく見たいか、工程の表を広く使いたいかは
 * 人によって違うため。
 *
 * 工程を持てるのは製作対象の明細だけ。運搬費・値引きなどの行は単価を明細に直接
 * 入力するので、ここには来ない（来た場合は明細一覧へ戻す）。
 *
 * 図面はまだ 1 枚も無いことがある。明細を足しただけでは図面を作らず、最初の
 * PDF を上げるときに作るため（[[drawings]]）。
 */
export default function QuotationItemDetail() {
  const { quotationId, quotationItemId } = useParams();
  const id = Number(quotationId);
  const itemId = Number(quotationItemId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const writable = canWrite(user, "transactional");
  const { containerRef, ratio, isResizing, startResize } = useResizableSplit(
    "quotation-item-detail",
  );
  const [pastRecordsOpen, setPastRecordsOpen] = useState(false);

  const quotationQuery = useQuery({
    queryKey: ["quotation", id],
    queryFn: () => getQuotation(id),
    enabled: Number.isFinite(id),
  });
  // 明細 1 件を引く API は無いが、1 回の問い合わせで全明細が取れるので一覧から拾う。
  const itemsQuery = useQuery({
    queryKey: ["quotation-items", id],
    queryFn: () => listQuotationItems(id),
    enabled: Number.isFinite(id),
  });

  /**
   * 見積完了の切り替え。片道にすると、間違えて完了にしたときに戻せなくなる。
   * 明細一覧の状態列からも同じフラグを切り替えられる。
   */
  const toggleCompleted = useMutation({
    mutationFn: (completed: boolean) => updateQuotationItem(id, itemId, { completed }),
    onSuccess: (updated) => {
      showSuccess(
        updated.completed ? "見積完了にしました。" : "見積完了を取り消しました。",
      );
      void queryClient.invalidateQueries({ queryKey: ["quotation-items", id] });
      void queryClient.invalidateQueries({ queryKey: ["quotations"] });
    },
    onError: (e: Error) => showError(e.message),
  });

  const quotation = quotationQuery.data;
  const item = itemsQuery.data?.items.find((row) => row.quotation_item_id === itemId);
  const backTo = `/quotations/${id}?tab=items`;
  /**
   * 除外した明細は読むだけにする。客先が発注しなかった行の金額を直せると、何を
   * 見積って何が落ちたのかが辿れなくなる（明細一覧のセルと同じ扱い。API も
   * 工程の追加を含めて断る前提で、ここは入口を閉じる）。
   */
  /**
   * 工程を直せるか。除外した行と、**受注済みの見積**は直せない（見積詳細と
   * 同じ決まり。写したあとに直しても案件は追従しないため）。
   */
  const editable =
    writable && item?.cancelled !== true && quotation?.status !== "ordered";

  if (quotationQuery.isPending || itemsQuery.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner label="読み込み中..." />
      </div>
    );
  }

  if (!quotation || !item) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <p className="text-default-600">指定された明細が見つかりませんでした。</p>
        <Button color="primary" onPress={() => navigate(backTo)}>
          明細一覧へ戻る
        </Button>
      </div>
    );
  }

  // 工程を積めるのは製作対象の行だけ。それ以外は単価を明細に直接入力する。
  if (item.drawing_number == null) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <p className="text-center text-default-600">
          この明細は製作対象ではないため、工程ごとの見積は作れません。
          <br />
          運搬費・値引きなどの単価は、明細の一覧で直接入力してください。
        </p>
        <Button color="primary" onPress={() => navigate(backTo)}>
          明細一覧へ戻る
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col px-6 pt-3 pb-3">
      <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-white shadow-md">
        <PageHeader
          title={`${item.sequence} 行目 ${item.drawing_number}`}
          parents={[
            { label: "見積管理", to: "/quotations" },
            { label: quotation.quotation_number, to: backTo },
          ]}
          actions={
            <>
              {/* 直せない理由はここでも言う。明細一覧から直接この画面に入る
                  ことがあり、見積のヘッダ（受注済の表示）を見ていない。 */}
              {quotation.status === "ordered" && (
                <span className="inline-flex items-center gap-1 text-tiny text-default-500">
                  <Lock size={14} className="text-default-400" />
                  受注済みのため工程は編集できません
                </span>
              )}
              {/* 値決めの材料。金額を入れる前に見るものなので、見積完了の左に
                  置く（押す順がそのまま左から右になる）。読むだけの操作なので
                  受注済みでも除外した行でも開ける。 */}
              <Button
                radius="full"
                variant="bordered"
                data-testid="item-past-records"
                startContent={<History size={18} />}
                onPress={() => setPastRecordsOpen(true)}
              >
                実績参照
              </Button>
              {item.cancelled ? (
                <Chip size="sm" variant="flat" color="default">
                  除外
                </Chip>
              ) : item.completed ? (
                <>
                  <Chip size="sm" variant="flat" color="success">
                    完了
                  </Chip>
                  <AuthorizedButton
                    scope="transactional"
                    radius="full"
                    variant="bordered"
                    data-testid="item-uncomplete"
                    startContent={<RotateCcw size={18} />}
                    // 受注済みは完了も動かせない（工程と同じ扱い）。
                    isDisabled={!editable}
                    isLoading={toggleCompleted.isPending}
                    onPress={() => toggleCompleted.mutate(false)}
                  >
                    完了を取り消す
                  </AuthorizedButton>
                </>
              ) : (
                <AuthorizedButton
                  scope="transactional"
                  radius="full"
                  color="primary"
                  data-testid="item-complete"
                  startContent={<ClipboardCheck size={18} />}
                  isDisabled={!editable}
                  isLoading={toggleCompleted.isPending}
                  onPress={() => toggleCompleted.mutate(true)}
                >
                  見積完了
                </AuthorizedButton>
              )}
            </>
          }
        />

        <div
          ref={containerRef}
          className="flex min-h-0 flex-1 border-t border-default-200"
        >
          <div style={{ width: `${ratio * 100}%` }} className="min-w-0">
            <ItemDrawingPanel
              drawingId={item.drawing_id}
              drawingNumber={item.drawing_number}
              itemName={item.item_name}
              // 図面は受注済みでも登録・差し替えできる（受注後に届く追図の
              // ため。足した図面は案件からも見える）。止めているのは工程のほう。
              canWrite={writable && item.cancelled !== true}
              isResizing={isResizing}
              // 版が増減すると明細一覧の版数が変わる。
              onChanged={() =>
                void queryClient.invalidateQueries({
                  queryKey: ["quotation-items", id],
                })
              }
              onCreateDrawing={async () => {
                const drawing = await createQuotationItemDrawing(id, itemId);
                // 明細の drawing_id が null から変わるので引き直す。
                await queryClient.invalidateQueries({
                  queryKey: ["quotation-items", id],
                });
                return drawing.drawing_id;
              }}
            />
          </div>

          {/* 左右の境目。掴んでいる間はブランドカラーで示す。 */}
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="左右の比率を変更"
            onMouseDown={startResize}
            className={[
              "w-1.5 shrink-0 cursor-col-resize touch-none select-none",
              isResizing ? "bg-brand-500" : "bg-default-200 hover:bg-brand-300",
            ].join(" ")}
          />

          <div className="min-w-0 flex-1">
            <ItemProcessPanel
              quotationId={id}
              itemId={itemId}
              quantity={item.quantity}
              canWrite={editable}
            />
          </div>
        </div>
      </section>

      <ItemPastRecordsDialog
        quotationId={id}
        itemId={itemId}
        drawingNumber={item.drawing_number}
        isOpen={pastRecordsOpen}
        // 写すのは工程なので、工程を直せる条件と揃える。
        canWrite={editable}
        onClose={() => setPastRecordsOpen(false)}
      />
    </div>
  );
}
